import { createEventStructureConstants } from "./event.structure";
import { createEventTicketConstants } from "./event.ticket";
import { getTemporaryGuid, parseValueToInt } from "services/tools";

const eventConstants = createEventStructureConstants();
const ticketConstants = createEventTicketConstants();
/**
 * Event Setting Date Item Object Definition
 * @typedef {Object} EventSettingDateItem
 * @property {string} id - The unique identifier for the date setting
 * @property {boolean} active - Whether the date setting is active
 * @property {number} order - The order of the date setting
 * @property {string} tag - The tag for the date setting
 * @property {string} subtag - The subtag for the date setting
 * @property {Array} add - Integer list of date add increment values (e.g. 7 for 7 days) to add from base date (see date constants)
 * @property {Array} act - Integer list of date action increment values (e.g. 7 for 7 days) to trigger actions (for runners flags a date launch override)
 */

/**
 * Create Event Setting Date Item Object
 * @param {Object} params
 * @param {boolean} [params.active=true] - Whether the date setting is active
 * @param {number} [params.order=1] - The order of the date setting
 * @param {string} [params.type="default"] - The type of the date setting (e.g. "default", "runners", "riders")
 * @returns {EventSettingDateItem} The created Event Setting Date Item Object
 */
export function createEventSettingDateItem({
  active = true,
  order = 1,
  type = "default",
}) {
  let id = getTemporaryGuid();
  let tag = "Date";
  let subtag = type;
  let add = [];
  let act = [];

  // boolean validations
  active = active ? true : false;

  // numeric validations
  order = parseValueToInt(order, 1);
  if (order <= 0) {
    order = 1;
  }

  // string validations
  const validSubtags = ["default", "riders"]; // default is runners
  if (!validSubtags.includes(subtag)) {
    subtag = "default";
  }

  return {
    id,
    active,
    order,
    tag,
    subtag,
    add,
    act,
  };
}

/**
 * Event Setting Ticket Item Object Definition
 * @typedef {Object} EventSettingTicketItem
 * @property {string} id - The unique identifier for the ticket setting
 * @property {boolean} active - Whether the ticket setting is active
 * @property {number} order - The order of the ticket setting
 * @property {string} tag - The tag for the ticket setting
 * @property {string} subtag - The subtag for the ticket setting
 * @property {string} name - The name of the ticket type
 * @property {number} price - The price of the ticket type
 * @property {number} min - The minimum quantity for the ticket type
 * @property {number} max - The maximum quantity for the ticket type
 */

/**
 * Create Event Setting Ticket Item Object
 * @param {Object} params
 * @param {boolean} [params.active=true] - Whether the ticket setting is active
 * @param {number} [params.order=1] - The order of the ticket setting
 * @param {string} [params.type="default"] - The type of the ticket setting (e.g. "default", "vip", "earlybird")
 * @param {string} [params.name="Standard"] - The name of the ticket type
 * @param {number} [params.price=0] - The price of the ticket type
 * @param {number} [params.groupSize=-1] - The maximum group size for the ticket type
 * @param {number} [params.capacity=-1] - The maximum quantity for the ticket type
 * @returns {EventSettingTicketItem} The created Event Setting Ticket Item Object
 */
export function createEventSettingTicketItem({
  active = true,
  order = 1,
  type = "default",
  name = "Standard",
  price = 0,
  groupSize = -1,
  capacity = -1,
}) {
  let id = getTemporaryGuid();
  let tag = "TicketType";
  let subtag = type;
  let min = groupSize;
  let max = capacity;

  // subtag validation
  let validSubtags = ["default"];
  if (!validSubtags.includes(subtag)) {
    subtag = "default";
  }

  // boolean validations
  active = active ? true : false;

  // numeric validations
  // note: price is always scaled to integer value (e.g. 1000 = $10.00)
  order = parseValueToInt(order, 1);
  if (order <= 0) {
    order = 1;
  }
  price = parseValueToInt(price, 0);
  if (price < 0) {
    price = 0;
  }
  min = parseValueToInt(min, ticketConstants.maxGroupSize);
  if (min <= 0) {
    min = ticketConstants.maxGroupSize;
  }
  max = parseValueToInt(max, ticketConstants.maxCapacity);
  if (max <= 0) {
    max = ticketConstants.maxCapacity;
  }

  // string validations
  if (typeof name !== "string" || name.trim() === "") {
    name = "Standard";
  }

  return {
    id,
    active,
    order,
    tag,
    subtag,
    name,
    price,
    min,
    max,
  };
}

/**
 * Event Setting Limit Item Object Definition
 * @typedef {Object} EventSettingLimitItem
 * @property {string} id - The unique identifier for the limit setting
 * @property {boolean} active - Whether the limit setting is active
 * @property {number} order - The order of the limit setting
 * @property {string} tag - The tag for the limit setting
 * @property {string} subtag - The subtag for the limit setting
 * @property {number} max - The maximum value for the limit
 * @property {string} unit - The unit for the limit (e.g. "days" or "quantity")
 */

/**
 * Create Event Setting Limit Item Object
 * @param {Object} params
 * @param {boolean} [params.active=true] - Whether the limit setting is active
 * @param {number} [params.order=1] - The order of the limit setting
 * @param {string} [params.type="capacity"] - The type of the limit setting (e.g. "capacity", "notice")
 * @param {number} [params.value] - The value for the limit (default: large integer)
 * @returns {EventSettingLimitItem} The created Event Setting Limit Item Object
 */
export function createEventSettingLimitItem({
  active = true,
  order = 1,
  type = "capacity",
  value = -1,
}) {
  let id = getTemporaryGuid();
  let tag = "Limit";
  let subtag = type;
  let max = value;

  // subtag validation with unit and default max assignment
  // set fallback defaultMax limits to max possible if not provided to prevent restrictions
  let unit, defaultMax;
  let validSubtags = ["capacity", "notice"];
  if (!validSubtags.includes(subtag)) {
    subtag = "capacity";
  }
  if (subtag === "notice") {
    unit = "days";
    defaultMax = eventConstants.midLaunchNotice;
  } else {
    unit = "quantity";
    defaultMax = eventConstants.midLaunchCapacity;
  }

  // boolean validations
  active = active ? true : false;

  // numeric validations
  order = parseValueToInt(order, 1);
  if (order <= 0) {
    order = 1;
  }
  max = parseValueToInt(max, defaultMax);
  if (max <= 0) {
    max = defaultMax;
  }

  return {
    id,
    active,
    order,
    tag,
    subtag,
    max,
    unit,
  };
}

/**
 * Event Setting Trigger Item Object Definition
 * @typedef {Object} EventSettingTriggerItem
 * @property {string} id - The unique identifier for the trigger setting
 * @property {boolean} active - Whether the trigger setting is active
 * @property {number} order - The order of the trigger setting
 * @property {string} tag - The tag for the trigger setting
 * @property {string} subtag - The subtag for the trigger setting
 * @property {number} min - The minimum value for the trigger
 * @property {string} unit - The unit for the trigger (e.g. "amount" or "quantity")
 */

/**
 * Create Event Setting Trigger Item Object
 * @param {Object} params
 * @param {boolean} [params.active=true] - Whether the trigger setting is active
 * @param {number} [params.order=1] - The order of the trigger setting
 * @param {string} [params.type="capacity"] - The type of the trigger setting (e.g. "financial", "capacity")
 * @param {number} [params.value] - The value for the trigger (default: large integer)
 * @returns {EventSettingTriggerItem} The created Event Setting Trigger Item Object
 */
export function createEventSettingTriggerItem({
  active = true,
  order = 1,
  type = "default",
  value = -1,
}) {
  let id = getTemporaryGuid();
  let tag = "Trigger";
  let subtag = type;
  let min = value;

  // subtag validation with unit and default min assignment
  // set fallback defaultMin triggers to max possible if not provided to prevent launch
  let unit, defaultMin;
  let validSubtags = ["default", "financial"];
  if (!validSubtags.includes(subtag)) {
    subtag = "default";
  }
  if (subtag === "financial") {
    unit = "amount";
    defaultMin = eventConstants.midLaunchFinancial;
  } else {
    unit = "quantity";
    defaultMin = eventConstants.midLaunchQuantity;
  }

  // boolean validations
  active = active ? true : false;

  // numeric validations
  // use values less
  order = parseValueToInt(order, 1);
  min = parseValueToInt(min, defaultMin);
  if (order <= 0) {
    order = 1;
  }
  if (min <= 0) {
    min = defaultMin;
  }

  return {
    id,
    active,
    order,
    tag,
    subtag,
    min,
    unit,
  };
}

/**
 * Event Setting Image Frame Item Object Definition
 * @typedef {Object} EventSettingImageFrameItem
 * @property {string} id - The unique identifier for the image frame setting
 * @property {boolean} active - Whether the image frame setting is active
 * @property {number} order - The order of the image frame setting
 * @property {string} tag - The tag for the image frame setting
 * @property {string} name - The name of the image frame setting
 * @property {string} description - The description of the image frame setting
 * @property {string} path - The image location id for the image frame setting
 */

/**
 * Create Event Setting Image Frame Item Object
 * @param {Object} params
 * @param {boolean} [params.active=true] - Whether the image frame setting is active
 * @param {number} [params.order=1] - The order of the image frame setting
 * @param {string} [params.name="Default"] - The name of the image frame setting
 * @param {string} [params.description=""] - The description of the image frame setting
 * @param {string} [params.path="none"] - The image location id for the image frame setting
 * @returns {EventSettingImageFrameItem} The created Event Setting Image Frame Item Object
 */
export function createEventSettingImageFrameItem({
  active = true,
  order = 1,
  type = "default",
  name = "Default",
  description = "",
  path = "none",
}) {
  let id = getTemporaryGuid();
  let tag = "frame";
  if (type === "custom") {
    tag = "frameCustom";
  }

  // boolean validations
  active = active ? true : false;

  // numeric validations
  order = parseValueToInt(order, 1);
  if (order <= 0) {
    order = 1;
  }

  // string validations
  if (typeof name !== "string" || name.trim() === "") {
    name = "Default";
  }
  if (typeof description !== "string") {
    description = "";
  }
  if (typeof path !== "string" || path.trim() === "") {
    path = "none";
  }

  return {
    id,
    active,
    order,
    tag,
    name,
    description,
    path,
  };
}
